/**
 * ThresholdDialog - Create/Edit product custom thresholds
 * Supports separate thresholds for Normal shops and Warehouses
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Search, X, Building2, Warehouse } from "lucide-react";

export default function ThresholdDialog({
  open,
  onOpenChange,
  mode, // "create" | "edit"
  product,
  onProductSelect,
  onProductClear,
  searchQuery,
  onSearchChange,
  searching,
  searchResults,
  normalMinQty,
  normalMaxQty,
  warehouseMinQty,
  warehouseMaxQty,
  onNormalMinChange,
  onNormalMaxChange,
  onWarehouseMinChange,
  onWarehouseMaxChange,
  onClearNormal,
  onClearWarehouse,
  saving,
  onSave,
}) {
  const isValid = product && (
    (normalMinQty && normalMaxQty) || (warehouseMinQty && warehouseMaxQty)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Set Product Threshold" : "Edit Threshold"}
          </DialogTitle>
          <DialogDescription>
            Configure separate min/max stock levels for Normal shops and Warehouses.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Product Selection (create mode) */}
          {mode === "create" && !product && (
            <div>
              <Label className="text-sm">Search Product</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Type product name or barcode..."
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="pl-8"
                  data-testid="threshold-product-search-input"
                />
              </div>
              {searching && <div className="text-xs text-muted-foreground mt-1">Searching...</div>}
              {searchResults.length > 0 && (
                <div className="border rounded mt-2 max-h-48 overflow-auto">
                  {searchResults.map(p => (
                    <div
                      key={p.id}
                      className="p-2 hover:bg-muted cursor-pointer text-sm"
                      onClick={() => onProductSelect(p)}
                      data-testid={`threshold-product-option-${p.id}`}
                    >
                      <div className="font-medium">{p.product}</div>
                      <div className="text-xs text-muted-foreground">{p.brand_name} · {p.auto_barcode}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selected Product Display */}
          {product && (
            <div className="bg-muted p-3 rounded">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium">{product.product}</div>
                  <div className="text-xs text-muted-foreground">
                    {product.brand_name} · {product.group_name}
                  </div>
                </div>
                {mode === "create" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2"
                    onClick={onProductClear}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Shop-Type Specific Threshold Inputs */}
          {product && (
            <>
              {/* Normal Shop Thresholds */}
              <div className="p-3 rounded-lg border bg-slate-50">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-slate-600" />
                  <span className="font-medium text-sm">Normal Shops</span>
                  <span className="text-xs text-muted-foreground">(Optional)</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Min Qty</Label>
                    <Input
                      type="number"
                      min="0"
                      value={normalMinQty}
                      onChange={(e) => onNormalMinChange(e.target.value)}
                      placeholder="e.g., 2"
                      className="h-8"
                      data-testid="threshold-normal-min"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Max Qty</Label>
                    <Input
                      type="number"
                      min="0"
                      value={normalMaxQty}
                      onChange={(e) => onNormalMaxChange(e.target.value)}
                      placeholder="e.g., 10"
                      className="h-8"
                      data-testid="threshold-normal-max"
                    />
                  </div>
                </div>
                {normalMinQty && normalMaxQty && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 mt-2 text-xs text-red-600"
                    onClick={onClearNormal}
                    data-testid="clear-normal-btn"
                  >
                    <X className="w-3 h-3 mr-1" /> Clear Normal
                  </Button>
                )}
              </div>

              {/* Warehouse Thresholds */}
              <div className="p-3 rounded-lg border bg-blue-50">
                <div className="flex items-center gap-2 mb-3">
                  <Warehouse className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-sm">Warehouses</span>
                  <span className="text-xs text-muted-foreground">(Optional)</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Min Qty</Label>
                    <Input
                      type="number"
                      min="0"
                      value={warehouseMinQty}
                      onChange={(e) => onWarehouseMinChange(e.target.value)}
                      placeholder="e.g., 5"
                      className="h-8"
                      data-testid="threshold-warehouse-min"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Max Qty</Label>
                    <Input
                      type="number"
                      min="0"
                      value={warehouseMaxQty}
                      onChange={(e) => onWarehouseMaxChange(e.target.value)}
                      placeholder="e.g., 30"
                      className="h-8"
                      data-testid="threshold-warehouse-max"
                    />
                  </div>
                </div>
                {warehouseMinQty && warehouseMaxQty && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 mt-2 text-xs text-red-600"
                    onClick={onClearWarehouse}
                    data-testid="clear-warehouse-btn"
                  >
                    <X className="w-3 h-3 mr-1" /> Clear Warehouse
                  </Button>
                )}
              </div>

              <div className="text-xs text-muted-foreground bg-amber-50 p-2 rounded border border-amber-200">
                <strong>How it works:</strong>
                <ul className="mt-1 space-y-1 list-disc list-inside">
                  <li>Configure thresholds for Normal shops, Warehouses, or both</li>
                  <li>A Normal shop custom threshold does NOT affect Warehouses</li>
                  <li>A Warehouse custom threshold does NOT affect Normal shops</li>
                  <li>Shop types without custom thresholds use the global defaults</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={!isValid || saving}
            data-testid="save-threshold-btn"
          >
            {saving ? "Saving..." : mode === "create" ? "Create Threshold" : "Update Threshold"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
